import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BellRing, Save, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InstallAppHint } from "@/components/settings/InstallAppHint";
import { PhoneDeviceControls } from "@/components/settings/PhoneDeviceControls";
import { QuietHoursControls } from "@/components/settings/QuietHoursControls";
import { WeeklySummaryButton } from "@/components/settings/WeeklySummaryButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhoneAlertsCard = ({ isOwner = false }) => {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [prefs, setPrefs] = useState({ mute_all: false, events: {}, quiet_enabled: false, quiet_start: "22:00", quiet_end: "07:00", timezone: "UTC", held_count: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/push/preferences`).then(({ data }) => {
      setPrefs({ ...data, mute_all: data.mute_all === true, events: data.events || {} });
      setTypes(Object.keys(data.events || {}));
    }).catch(() => toast.error(t("personalSettings.loadFailed"))).finally(() => setLoading(false));
  }, [t]);

  const save = async () => {
    setSaving(true);
    try {
      const { held_count, ...body } = prefs;
      if (body.quiet_enabled) body.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || body.timezone;
      const { data } = await axios.put(`${API}/push/preferences`, body);
      setPrefs((p) => ({ ...p, ...data }));
      toast.success(t("phoneAlerts.saved"));
    }
    catch { toast.error(t("phoneAlerts.saveFailed")); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-sm border-slate-200 shadow-none" data-testid="phone-alerts-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-slate-500" /> {t("phoneAlerts.title")}
        </CardTitle>
        <p className="text-xs text-slate-500">{t("phoneAlerts.subtitle")}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <InstallAppHint />
        <PhoneDeviceControls />
        <div className="flex items-center justify-between gap-4 py-2 border-t border-slate-100">
          <p className="text-sm font-medium text-slate-800 flex items-center gap-2"><BellRing className="w-3.5 h-3.5" /> {t("phoneAlerts.muteAll")}</p>
          <Switch data-testid="phone-alerts-mute-all" aria-label={t("phoneAlerts.muteAll")} disabled={loading} checked={prefs.mute_all} onCheckedChange={(v) => setPrefs((p) => ({ ...p, mute_all: v }))} />
        </div>
        <QuietHoursControls prefs={prefs} setPrefs={setPrefs} disabled={loading} />
        {isOwner && <WeeklySummaryButton />}
        {!loading && !prefs.mute_all && (
          <div className="space-y-1" data-testid="phone-alerts-event-list">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("phoneAlerts.choose")}</p>
            {types.map((key) => (
              <div key={key} className="flex items-center justify-between gap-4 py-1.5">
                <span className="text-sm text-slate-700">{t(`personalSettings.eventLabels.${key}`, { defaultValue: key })}</span>
                <Switch data-testid={`phone-alerts-event-${key}`} aria-label={t(`personalSettings.eventLabels.${key}`, { defaultValue: key })}
                  checked={prefs.events[key] !== false} onCheckedChange={(v) => setPrefs((p) => ({ ...p, events: { ...p.events, [key]: v } }))} />
              </div>
            ))}
          </div>
        )}
        <Button data-testid="phone-alerts-save" onClick={save} disabled={saving || loading} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-sm w-full">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? t("settings.saving") : t("phoneAlerts.save")}
        </Button>
      </CardContent>
    </Card>
  );
};
